import { useQuery } from 'react-query';
import axios from 'axios';
import slugify from 'slugify';
import _ from 'lodash';
import jsonpAdapter from '../logic/axios-jsonp';
import { getSolrUrls } from './utils';
import { getProject } from '../views/common/utils';
import { GetSessionID } from '../main/MandalaSession';

const solr_urls = getSolrUrls();

export function useSearch(
    searchText = '',
    start = 0,
    rows = 0,
    facetType = 'all',
    facetOffset = 0,
    facetLimit = 0,
    facetBuckets = false,
    filters = [],
    enabled = true
) {
    // searchText = searchText.replace(/"/g, '');

    /*
    if (!searchText.includes('advSearch:')) {
        searchText = enhanceBasicQuery(searchText);
        console.log("new search text", searchText);
    }*/
    /*
    console.log("params in useSearch", [
        searchText,
        start,
        rows,
        facetType,
        facetOffset,
        facetLimit,
        facetBuckets,
        filters,
        enabled
    ]);
     */
    return useQuery(
        [
            'search',
            start,
            rows,
            facetType,
            facetOffset,
            facetLimit,
            facetBuckets,
            filters,
            searchText,
            // slugify(searchText), // this is not working. it seems to always return ""
        ],
        () =>
            getSearchData(
                searchText,
                start,
                rows,
                facetType,
                facetOffset,
                facetLimit,
                facetBuckets,
                filters
            ),
        { keepPreviousData: true, enabled }
    );
}

/* unused ....
function enhanceBasicQuery(query) {
    let newq =
        `advSearch: (text:"${query}" OR (title:${query}^100 title:${query}/^100 title:${query}*^80 names_txt:${query}^90 ` +
        `names_txt:${query}*^70 (names_txt:${query}* AND asset_type:terms)^80))`;
    return newq;
}
 */

async function getSearchData(
    searchText,
    start,
    rows,
    facetType,
    facetOffset,
    facetLimit,
    facetBuckets,
    filters
) {
    let params = {
        fl: '*',
        wt: 'json',
        echoParams: 'explicit',
        indent: 'true',
        start: start,
        rows: rows,
        'json.facet': JSON.stringify(
            getJsonFacet(facetType, facetOffset, facetLimit, facetBuckets)
        ),
    };

    const sid = GetSessionID();
    if (sid) {
        params.sid = sid;
    }

    let queryParams = {};
    if (searchText.startsWith('advSearch')) {
        const newSearchText = searchText.substring(10);
        queryParams = {
            q: newSearchText,
        };
    } else {
        queryParams = constructTextQuery(searchText);
    }

    const filterByProject = !searchText.includes('name_deva');
    const filterParams = constructFilters(filters, filterByProject);
    params = { ...params, ...queryParams, ...filterParams };

    // Include Tibetan sorting as second sorting condition after rank/relevancy
    params['sort'] = 'score desc, cascading_position_i asc, title_sort_s asc';

    // Than added 2022/04/22 to get Tibetan Terms results to sort properly.
    // Tests if search is filtering by Terms (only can sort terms this way)
    if (filters?.length > 0 && filters[0]?.id === 'asset_type:terms') {
        // If Terms Sort constant is set, use that to sort terms (for Tibetan this is 'cascading_position_i')
        if (
            process.env?.REACT_APP_TERMS_SORT &&
            process.env?.REACT_APP_TERMS_SORT.length > 0
        ) {
            params.sort = `score DESC, ${process.env?.REACT_APP_TERMS_SORT} ASC`;
            // If the sort param is cascading_position_i filter out bad records that don't have that value
            if (
                process.env?.REACT_APP_TERMS_SORT === 'cascading_position_i' &&
                Array.isArray(params?.fq)
            ) {
                params.fq.push('cascading_position_i:*');
            }
        }
    }

    // console.log("Params in useSearch", params);
    const request = {
        adapter: jsonpAdapter,
        callbackParamName: 'json.wrf',
        url: solr_urls.assets,
        params: params,
    };
    // console.log("the use search request", request);

    const { data } = await axios.request(request);

    return data;
}

function getJsonFacet(facetType, offset, limit, buckets) {
    return {
        ...((facetType === 'all' || facetType === 'asset_count') && {
            asset_count: {
                type: 'terms',
                field: 'asset_type',
                limit,
                offset,
                //sort: ff['asset_type']?.sort || 'count desc',
                domain: { excludeTags: 'asset_type' },
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'related_subjects') && {
            related_subjects: {
                type: 'terms',
                field: 'kmapid_subjects_idfacet',
                limit,
                offset,
                //sort: ff['subjects']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'related_places') && {
            related_places: {
                type: 'terms',
                field: 'kmapid_places_idfacet',
                limit,
                offset,
                //sort: ff['places']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'related_terms') && {
            related_terms: {
                type: 'terms',
                field: 'kmapid_terms_idfacet',
                limit,
                offset,
                //sort: ff['terms']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'feature_types') && {
            feature_types: {
                type: 'terms',
                field: 'feature_types_idfacet',
                limit,
                offset,
                //sort: ff['feature_types']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'languages') && {
            languages: {
                type: 'terms',
                field: 'node_lang',
                limit,
                offset,
                //sort: ff['languages']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'collections') && {
            collections: {
                type: 'terms',
                field: 'collection_idfacet',
                limit,
                offset,
                //sort: ff['collections']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'node_user') && {
            node_user: {
                type: 'terms',
                field: 'node_user_full_s',
                limit,
                offset,
                //sort: ff['user']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'creator') && {
            creator: {
                type: 'terms',
                field: 'creator',
                limit,
                offset,
                //sort: ff['creator']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'perspective') && {
            perspective: {
                type: 'terms',
                field: 'perspectives_ss',
                limit,
                offset,
                //sort: ff['perspective']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
        ...((facetType === 'all' || facetType === 'associated_subjects') && {
            associated_subjects: {
                type: 'terms',
                field: 'associated_subject_map_idfacet',
                limit,
                offset,
                //sort: ff['associated_subjects']?.sort || 'count desc',
                numBuckets: buckets,
            },
        }),
    };
}

/**
 * Exported function to construct the text query. Used by useInfiniteSearch.js hook and SearchBuilder.js class.
 *
 * @param searchString
 * @returns {{q: (string|string), slashy: string, xact: *, search: (string|string), starts: (string|string)}}
 */
export function constructTextQuery(searchString) {
    // console.log("Search string in construct query", searchString);
    const NO_SEARCH = 'None';
    // there are similar allwords variables in SearchBuilder and useInfiniteSearch
    const sswords = searchString?.toLowerCase().trim().split(' ');
    const allwords = sswords.join(' AND ');

    let searchstring = (searchString || '').toLowerCase();

    // remove escapes already in place just for simplicity
    // console.log('construct query search string', searchstring);
    // console.log (JSON.stringify(state));
    let starts = searchstring.length ? searchstring + '*' : '*';
    let search = searchstring.length ? '*' + searchstring + '*' : '*';
    let slashy = searchstring + '/';
    if (!searchString || searchstring.length === 0) {
        search = slashy = '*';
        searchstring = NO_SEARCH;
    }
    let xact = searchstring;
    if (xact.at(-1) === '་') {
        xact = xact.substring(0, xact.length - 1);
    }

    // If blank search (i.e. for side bar when loading page etc.) do not execute code below
    let basic_req = {};
    if (searchstring === NO_SEARCH) {
        basic_req = {
            q: `*:*`,
            xact: '*',
            starts: '*',
            search: '*',
            slashy: '*',
        };
        return basic_req;
    }

    // ss_for_partial (search string for partial match) finds "Wind in the Willows" when searching on "Wind in the Will"
    //      See https://uvaissues.atlassian.net/browse/MANU-7645
    // Used also to find SOLR special characters that need to be escaped.
    //      See https://uvaissues.atlassian.net/browse/MANU-7653
    const contains_special = hasSolrSpecialChar(searchstring);
    const ss_for_partial = escapeSolrSpecialChar(searchstring);
    const beginsWith = `${ss_for_partial}*`;
    const partial = `*${ss_for_partial}*`;
    const xactescaped = escapeSolrSpecialChar(xact);
    const slashescaped = escapeSolrSpecialChar(slashy);
    // console.log('xact escaped', xactescaped);
    // const allescaped = escapeSolrSpecialChar(allwords); // All word searches don't seem to work

    const qparts = [];
    // Exact matches on copy field "str"
    if (xactescaped?.length > 0 && !contains_special) {
        // qparts.push(`(names:${xactescaped}* AND asset_type:terms)^100`);  // was prioritizing term matches but Newman asked to remove
        qparts.push(`(str:${xactescaped})^100`);
        qparts.push(`(str:${slashescaped})^100`);
    }
    // Begins with on str next
    if (beginsWith?.length > 1) {
        qparts.push(`(str:${beginsWith})^95`);
        qparts.push(`(str:${partial})^20`);
    }

    // String anywhere last
    qparts.push(`text:${xactescaped}`); // prevents multiple matches from getting an extra boost, sets all contained to same value

    basic_req = {
        // search: tweak for scoping later
        // To get matched terms to show first. Tried ' asset_type:terms^70' but then it shows ALL terms first
        // search string should match useInfiniteSearch.js line 310.
        q: searchstring === NO_SEARCH ? `*:*` : qparts.join(' '),
        xact,
        starts,
        search,
        slashy,
    };
    // console.log("basic_request in useSearch", basic_req);
    return basic_req;
}

// Functions below are for escaping special characters in SOLR
// See https://solr.apache.org/guide/7_3/the-standard-query-parser.html#escaping-special-characters
const SOLR_SPECIAL_CHAR = '\\+-&|!(){}[]^"~*?:/'; // (double) backslash must be listed first because it is the escape character for the others

function escapeSolrSpecialChar(sstr) {
    for (let cn in SOLR_SPECIAL_CHAR) {
        let chr = SOLR_SPECIAL_CHAR[cn];
        let re = new RegExp('\\' + chr, 'g');
        sstr = sstr.replace(re, '\\' + chr);
    }
    let re = /\s+/g;
    return sstr.replace(re, '\\ ');
}

function hasSolrSpecialChar(sstr) {
    for (let cn in SOLR_SPECIAL_CHAR) {
        let chr = SOLR_SPECIAL_CHAR[cn];
        if (sstr.includes(chr)) {
            return true;
        }
    }
    return false;
}

function stripSolrSpecialChar(sstr) {
    for (let cn in SOLR_SPECIAL_CHAR) {
        let chr = SOLR_SPECIAL_CHAR[cn];
        let re = new RegExp('\\' + chr, 'g');
        sstr = sstr.replace(re, '');
    }
    sstr = sstr.replace(/\+/g, '');
    return sstr;
}

function constructFilters(filters, filterProject = true) {
    //console.log('Filters', filters);
    // If no filters are passed then we return the all the assets.
    if (_.isEmpty(filters)) {
        // Old fqs filters out grouping terms (letters, 9311, and phrases, 9314, 9667 - English letters) for terms trees,
        // const xrelated = ['subjects-9311', 'subjects-9314', 'subjects-9667'];
        // Current fqs filters for only accepted asset type or terms that are expressions (subjects-9315)
        const fqs = [
            'asset_type:(audio-video images texts visuals sources subjects places collections) OR ' +
                '(asset_type:terms AND related_uid_ss:(subjects-9315 subjects-9668 subjects-9669 subjects-10525))',
        ];

        // Added by Than for project filtering
        const projid = getProject();
        if (projid && filterProject) {
            fqs.push(`projects_ss:${projid}`);
        }
        return {
            fq: fqs,
        };
    }

    // console.log('constructFilters: received filters: ', filters);
    const hashedFilters = arrayToHash(filters, 'field');
    // console.log('constructFilters: sorted filters = ', hashedFilters);

    const facets = Object.keys(hashedFilters);
    // console.log('constructFilters: keys = ', facets);

    let fq_list = [];

    function constructFQs(facetData, fieldName) {
        let fq_list = [];
        let not_list = [];
        let and_list = [];
        let or_list = [];

        facetData.forEach((f) => {
            if (f.operator === 'NOT') {
                not_list.push(
                    '(*:* AND -' + fieldName + ':("' + f.match + '"))'
                );
            } else if (f.operator === 'AND') {
                and_list.push(
                    '(*:* AND ' + fieldName + ':("' + f.match + '"))'
                );
            } else {
                /* OR default case */
                or_list.push('(*:* OR ' + fieldName + ':("' + f.match + '"))');
            }
        });

        const or_clause =
            '{!tag=' +
            fieldName +
            '}' +
            fieldName +
            ':' +
            '(' +
            or_list.join(' ') +
            ')';

        // TODO: does the order matter?
        if (or_list.length) fq_list.push(or_clause);
        if (and_list.length) fq_list.push(...and_list);
        if (not_list.length) fq_list.push(...not_list);

        fq_list.push(
            '-related_uid_ss:(subjects-9311 subjects-9314 subjects-9667)'
        );
        // console.log('constructFQs returning: ', fq_list);
        return fq_list;
    }

    // TODO: Refactor so that facets can be added via configuration.
    facets.forEach((facet) => {
        const facetData = hashedFilters[facet];
        let fqs = [];
        // console.log('constructFilters:\tfacet ' + facet + ' = ', facetData);
        switch (facet) {
            case 'asset_type':
                fqs = constructFQs(facetData, 'asset_type');
                fq_list.push(...fqs);
                break;
            case 'subjects':
                fqs = constructFQs(facetData, 'kmapid');
                fq_list.push(...fqs);
                break;
            case 'places':
                fqs = constructFQs(facetData, 'kmapid');
                fq_list.push(...fqs);
                break;
            case 'terms':
                fqs = constructFQs(facetData, 'kmapid');
                fq_list.push(...fqs);
                break;
            case 'languages':
                fqs = constructFQs(facetData, 'language');
                fq_list.push(...fqs);
                break;
            case 'feature_types':
                fqs = constructFQs(facetData, 'kmapid');
                fq_list.push(...fqs);
                break;
            case 'users':
                fqs = constructFQs(facetData, 'node_user_full_s');
                fq_list.push(...fqs);
                break;
            case 'creator':
                fqs = constructFQs(facetData, 'creator');
                fq_list.push(...fqs);
                break;
            case 'collections':
                fqs = constructFQs(facetData, 'collection_uid_path_ss');
                fq_list.push(...fqs);
                break;
            case 'perspective':
                fqs = constructFQs(facetData, 'perspectives_ss');
                fq_list.push(...fqs);
                break;
            case 'associated_subjects':
                fqs = constructFQs(facetData, 'associated_subject_map_idfacet');
                fq_list.push(...fqs);
                break;
            default:
                console.error('UNHANDLED FACET TYPE: ' + facet);
                break;
        }
    });

    // Added by Than for project filtering
    const projid = getProject();
    if (projid) {
        fq_list.push(`projects_ss:${projid}`);
    }

    // console.log('RETURNING FQ_LIST = ', fq_list);
    return { fq: fq_list };
}

function arrayToHash(array, keyField) {
    // console.log('received: ', array);
    if (!array) {
        array = [];
    }
    return array.reduce((collector, item) => {
        const key = item[keyField] || 'unknown key';
        if (!collector[key]) {
            collector[key] = [];
        }
        collector[key].push(item);
        return collector;
    }, {});
}

/*
Replaced by escapedSolrSpecialChar() above
function escapeSearchString(str) {
    str = str.replace(/ /g, '\\ '); // escape spaces
    str = str.replace('(', '\\(');
    str = str.replace(')', '\\)');
    str = str.replace(':', '\\:');
    str = str.replace('+', '\\+');
    str = str.replace('-', '\\-');
    str = str.replace('"', '\\"');
    str = str.replace('?', '\\?');
    return str;
}

 */
